/**
 * Team Spain v0 — Modal de Gestión de Convocatoria del Evento (M2)
 *
 * Permite al staff técnico convocar atletas y staff del equipo:
 * - Selección múltiple con búsqueda y filtros por unidad.
 * - Soporte para atletas sin cuenta de acceso ("Sin cuenta").
 * - Conservación íntegra de respuestas (RSVP) existentes.
 * - Retirada trazable (withdrawn) sin borrado físico destructivo.
 * - Validación estricta en backend.
 */

import React, { useState, useEffect, useMemo } from 'react'
import {
  Search,
  CheckSquare,
  Square,
  AlertCircle,
  Info,
} from 'lucide-react'
import { EventItem, RosterCandidate } from '../../types/calendar'
import { calendarService } from './calendarService'
import { Modal } from '../shared/Modal'

interface ManageRosterModalProps {
  event: EventItem
  onClose: () => void
  onSuccess: (summary: string) => void
}

type UnitFilter = 'all' | 'offense' | 'defense' | 'staff' | 'no_account'

export const ManageRosterModal: React.FC<ManageRosterModalProps> = ({
  event,
  onClose,
  onSuccess,
}) => {
  const [candidates, setCandidates] = useState<RosterCandidate[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Filtros
  const [searchQuery, setSearchQuery] = useState('')
  const [unitFilter, setUnitFilter] = useState<UnitFilter>('all')
  const initialSelectedRef = React.useRef<Set<string> | null>(null)

  // Cargar candidatos al abrir
  useEffect(() => {
    let isMounted = true
    const loadCandidates = async () => {
      setLoading(true)
      setErrorMsg(null)
      try {
        const data = await calendarService.getRosterCandidates(event.team_id, event.id)
        if (!isMounted) return
        setCandidates(data)

        // Preseleccionar los que ya están activamente convocados (no retirados)
        const initialSelected = new Set<string>()
        data.forEach((c) => {
          if (c.current_status && c.current_status !== 'withdrawn') {
            initialSelected.add(c.id)
          }
        })
        initialSelectedRef.current = new Set(initialSelected)
        setSelectedIds(initialSelected)
      } catch (err: unknown) {
        if (!isMounted) return
        setErrorMsg(err instanceof Error ? err.message : 'Error al cargar candidatos de la plantilla.')
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadCandidates()
    return () => {
      isMounted = false
    }
  }, [event.id, event.team_id])

  // Filtrado de candidatos
  const filteredCandidates = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return candidates.filter((c) => {
      // Filtro de texto
      if (q) {
        const matchesName = c.name.toLowerCase().includes(q)
        const matchesDorsal = c.dorsal_number?.toString() === q
        const matchesPos = c.position?.toLowerCase().includes(q)
        const matchesEmail = c.user_email?.toLowerCase().includes(q)
        if (!matchesName && !matchesDorsal && !matchesPos && !matchesEmail) {
          return false
        }
      }

      // Filtro de categoría / unidad
      if (unitFilter === 'offense') return c.unit === 'offense'
      if (unitFilter === 'defense') return c.unit === 'defense'
      if (unitFilter === 'staff') return c.type === 'staff'
      if (unitFilter === 'no_account') return !c.has_account

      return true
    })
  }, [candidates, searchQuery, unitFilter])

  // Métricas del resumen antes de guardar
  const summaryMetrics = useMemo(() => {
    let newCount = 0
    let preservedCount = 0
    let withdrawnCount = 0

    candidates.forEach((c) => {
      const isSelected = selectedIds.has(c.id)
      const wasActive = c.current_status && c.current_status !== 'withdrawn'

      if (isSelected) {
        if (!wasActive) {
          newCount++
        } else {
          preservedCount++
        }
      } else {
        if (wasActive) {
          withdrawnCount++
        }
      }
    })

    return {
      totalSelected: selectedIds.size,
      newCount,
      preservedCount,
      withdrawnCount,
    }
  }, [candidates, selectedIds])

  const hasUnsavedChanges = useMemo(() => {
    if (!initialSelectedRef.current) return false
    if (selectedIds.size !== initialSelectedRef.current.size) return true
    for (const id of selectedIds) {
      if (!initialSelectedRef.current.has(id)) return true
    }
    return false
  }, [selectedIds])

  // Toggle individual
  const toggleCandidate = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // Acciones rápidas de selección
  const selectAll = () => {
    setSelectedIds(new Set(candidates.map((c) => c.id)))
  }

  const deselectAll = () => {
    setSelectedIds(new Set())
  }

  const selectOnlyUnit = (unit: 'offense' | 'defense' | 'staff') => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      candidates.forEach((c) => {
        if ((unit === 'staff' && c.type === 'staff') || c.unit === unit) {
          next.add(c.id)
        }
      })
      return next
    })
  }

  // Guardar convocatoria
  const handleSaveRoster = async () => {
    if (saving) return
    setSaving(true)
    setErrorMsg(null)

    const selectedAthleteIds = candidates
      .filter((c) => c.type === 'athlete' && c.athlete_id && selectedIds.has(c.id))
      .map((c) => c.athlete_id!)

    const selectedStaffUserIds = candidates
      .filter((c) => c.type === 'staff' && c.user_id && selectedIds.has(c.id))
      .map((c) => c.user_id!)

    try {
      const res = await calendarService.manageEventRoster(
        event.id,
        selectedAthleteIds,
        selectedStaffUserIds
      )

      onSuccess(
        `Convocatoria actualizada: ${res.total_active} participantes activos (${res.new_count} nuevos, ${res.preserved_count} respuestas conservadas, ${res.withdrawn_count} retirados).`
      )
      onClose()
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al guardar convocatoria.')
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Gestionar Convocatoria"
      subtitle={`${event.title} — Selección de jugadores y staff técnico`}
      size="lg"
      hasUnsavedChanges={hasUnsavedChanges}
      footer={
        <div style={{ width: '100%' }}>
          {/* Resumen del impacto de cambios */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1.25rem',
              flexWrap: 'wrap',
              marginBottom: '0.75rem',
              padding: '0.65rem 0.75rem',
              backgroundColor: 'var(--surface-subtle)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.78rem',
            }}
          >
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Total convocados:</span>{' '}
              <strong style={{ color: 'var(--text-main)', fontSize: '0.85rem' }}>
                {summaryMetrics.totalSelected}
              </strong>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Nuevos:</span>{' '}
              <strong style={{ color: 'var(--status-info-text)' }}>
                +{summaryMetrics.newCount}
              </strong>{' '}
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>(pendientes)</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Mantenidos:</span>{' '}
              <strong style={{ color: 'var(--status-success-text)' }}>
                {summaryMetrics.preservedCount}
              </strong>{' '}
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>(respuestas intactas)</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>A retirar:</span>{' '}
              <strong style={{ color: 'var(--status-danger-text)' }}>
                {summaryMetrics.withdrawnCount}
              </strong>{' '}
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>(historial guardado)</span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <Info size={14} />
              <span>
                Al guardar, los nuevos participantes quedan como "Pendiente". Las respuestas y notas previas se conservan.
              </span>
            </div>

            <div style={{ display: 'flex', gap: '0.65rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={saving}
                style={{ fontSize: '0.84rem' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveRoster}
                disabled={saving || loading}
                style={{ fontSize: '0.84rem', minWidth: '160px' }}
              >
                {saving ? 'Guardando...' : `Guardar convocatoria (${summaryMetrics.totalSelected})`}
              </button>
            </div>
          </div>
        </div>
      }
    >

        {/* Mensaje de error */}
        {errorMsg && (
          <div
            style={{
              margin: '0.75rem 1.25rem 0',
              padding: '0.65rem 0.85rem',
              backgroundColor: 'var(--status-danger-bg)',
              color: 'var(--status-danger-text)',
              border: '1px solid var(--status-danger-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.82rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <AlertCircle size={16} style={{ flexShrink: 0 }} />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Filtros y Búsqueda */}
        <div style={{ padding: '0.85rem 1.25rem', backgroundColor: 'var(--surface-subtle)', borderBottom: '1px solid var(--surface-border)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', marginBottom: '0.65rem' }}>
            <div style={{ position: 'relative', flex: '1 1 240px' }}>
              <Search
                size={16}
                style={{
                  position: 'absolute',
                  left: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                }}
              />
              <input
                type="text"
                className="form-input"
                style={{ paddingLeft: '2.2rem', fontSize: '0.85rem' }}
                placeholder="Buscar por nombre, dorsal o posición..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
              <button
                type="button"
                onClick={() => setUnitFilter('all')}
                className={`badge ${unitFilter === 'all' ? 'badge-primary' : 'badge-muted'}`}
                style={{ cursor: 'pointer', padding: '0.4rem 0.65rem', fontSize: '0.78rem' }}
              >
                Todos ({candidates.length})
              </button>
              <button
                type="button"
                onClick={() => setUnitFilter('offense')}
                className={`badge ${unitFilter === 'offense' ? 'badge-primary' : 'badge-muted'}`}
                style={{ cursor: 'pointer', padding: '0.4rem 0.65rem', fontSize: '0.78rem' }}
              >
                Ataque ({candidates.filter((c) => c.unit === 'offense').length})
              </button>
              <button
                type="button"
                onClick={() => setUnitFilter('defense')}
                className={`badge ${unitFilter === 'defense' ? 'badge-primary' : 'badge-muted'}`}
                style={{ cursor: 'pointer', padding: '0.4rem 0.65rem', fontSize: '0.78rem' }}
              >
                Defensa ({candidates.filter((c) => c.unit === 'defense').length})
              </button>
              <button
                type="button"
                onClick={() => setUnitFilter('staff')}
                className={`badge ${unitFilter === 'staff' ? 'badge-primary' : 'badge-muted'}`}
                style={{ cursor: 'pointer', padding: '0.4rem 0.65rem', fontSize: '0.78rem' }}
              >
                Staff ({candidates.filter((c) => c.type === 'staff').length})
              </button>
              <button
                type="button"
                onClick={() => setUnitFilter('no_account')}
                className={`badge ${unitFilter === 'no_account' ? 'badge-primary' : 'badge-muted'}`}
                style={{ cursor: 'pointer', padding: '0.4rem 0.65rem', fontSize: '0.78rem' }}
              >
                Sin cuenta ({candidates.filter((c) => !c.has_account).length})
              </button>
            </div>
          </div>

          {/* Acciones Rápidas */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', fontSize: '0.76rem' }}>
            <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Selección rápida:</span>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={selectAll}
              style={{ padding: '0.2rem 0.5rem', fontSize: '0.74rem' }}
            >
              Seleccionar Todos
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => selectOnlyUnit('offense')}
              style={{ padding: '0.2rem 0.5rem', fontSize: '0.74rem' }}
            >
              + Ataque
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => selectOnlyUnit('defense')}
              style={{ padding: '0.2rem 0.5rem', fontSize: '0.74rem' }}
            >
              + Defensa
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => selectOnlyUnit('staff')}
              style={{ padding: '0.2rem 0.5rem', fontSize: '0.74rem' }}
            >
              + Staff
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={deselectAll}
              style={{ padding: '0.2rem 0.5rem', fontSize: '0.74rem', color: 'var(--status-danger-text)' }}
            >
              Deseleccionar todos
            </button>
          </div>
        </div>

        {/* Lista de candidatos */}
        <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: '1rem 1.25rem' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
              Cargando atletas y staff del equipo...
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-muted)' }}>
              No se encontraron miembros con los filtros seleccionados.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.65rem' }}>
              {filteredCandidates.map((candidate) => {
                const isSelected = selectedIds.has(candidate.id)
                const isAthlete = candidate.type === 'athlete'
                const isWithoutAccount = isAthlete && !candidate.has_account

                return (
                  <div
                    key={candidate.id}
                    onClick={() => toggleCandidate(candidate.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.65rem 0.85rem',
                      borderRadius: 'var(--radius-sm)',
                      border: isSelected
                        ? '1.5px solid var(--brand-red)'
                        : '1px solid var(--surface-border)',
                      backgroundColor: isSelected
                        ? 'var(--surface-card)'
                        : 'var(--surface-subtle)',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease-in-out',
                    }}
                  >
                    <div style={{ color: isSelected ? 'var(--brand-red)' : 'var(--text-muted)' }}>
                      {isSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: '0.86rem',
                            color: 'var(--text-main)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {candidate.name}
                        </span>

                        {candidate.dorsal_number !== undefined && (
                          <span
                            style={{
                              fontFamily: 'monospace',
                              fontWeight: 700,
                              fontSize: '0.78rem',
                              color: 'var(--brand-navy)',
                              backgroundColor: 'rgba(2, 6, 23, 0.08)',
                              padding: '0.05rem 0.35rem',
                              borderRadius: '3px',
                            }}
                          >
                            #{candidate.dorsal_number}
                          </span>
                        )}
                      </div>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.2rem', alignItems: 'center' }}>
                        {candidate.position && (
                          <span className="badge badge-muted" style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem' }}>
                            {candidate.position}
                          </span>
                        )}

                        <span
                          className={`badge ${
                            candidate.unit === 'offense'
                              ? 'badge-warning'
                              : candidate.unit === 'defense'
                                ? 'badge-primary'
                                : 'badge-muted'
                          }`}
                          style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem', textTransform: 'capitalize' }}
                        >
                          {candidate.unit === 'offense'
                            ? 'Ataque'
                            : candidate.unit === 'defense'
                              ? 'Defensa'
                              : candidate.type === 'staff'
                                ? `Staff (${candidate.role})`
                                : 'Equipo'}
                        </span>

                        {isWithoutAccount && (
                          <span
                            className="badge badge-muted"
                            title="Atleta en el roster federativo sin cuenta de usuario creada"
                            style={{
                              fontSize: '0.68rem',
                              padding: '0.1rem 0.35rem',
                              backgroundColor: 'rgba(100, 116, 139, 0.15)',
                              color: 'var(--text-secondary)',
                            }}
                          >
                            Sin cuenta
                          </span>
                        )}

                        {candidate.current_status && (
                          <span
                            style={{
                              fontSize: '0.68rem',
                              fontWeight: 600,
                              color:
                                candidate.current_status === 'confirmed'
                                  ? 'var(--status-success-text)'
                                  : candidate.current_status === 'declined'
                                    ? 'var(--status-danger-text)'
                                    : candidate.current_status === 'withdrawn'
                                      ? 'var(--text-muted)'
                                      : 'var(--status-warning-text)',
                            }}
                          >
                            {candidate.current_status === 'confirmed'
                              ? '✓ Confirmada'
                              : candidate.current_status === 'declined'
                                ? '✕ No asiste'
                                : candidate.current_status === 'withdrawn'
                                  ? 'Retirado'
                                  : '⏱ Pendiente'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
    </Modal>
  )
}
